
































































// is used for day/week types
import { Vue, Component, Model, Prop, Ref, Watch, Inject } from 'vue-property-decorator';

import DetailedCalendarSlot from './DetailedCalendarSlot.vue';
import CalendarAction from '../CalendarAction.vue';
import IconRemove from '@/components/icons/Remove.vue';

import { ZERO_BASED_WEEKDAYS } from '../consts';
import { formatDayToHumanText, getWeekRange, getDayRange, createDatetimeRangeChecker } from '@/utils/date';

import { CalendarType, Slot, HighlightSlot, SlotType, SlotsMap, SlotsClinicMap } from '../types';
import { CalendarRef, CalendarRange, CalendarDate, ParsedEvent } from '../lib-types';
import { ISOCalendarDate, ISODateTimeRange } from '@/utils/date/types';
import { Maybe } from '@/types/common';

@Component({
    components: {
        DetailedCalendarSlot,
        CalendarAction,
        IconRemove,
    }
})
export default class DetailedCalendar extends Vue {
    @Model('update:date', { type: String }) date!: string
    @Prop({ default: () => ({}) }) slots!: SlotsMap
    @Prop({ default: () => ({}) }) slotsClinic!: SlotsClinicMap
    @Prop({ default: CalendarType.Day }) type!: CalendarType.Day | CalendarType.Week;
    @Prop({ default: '' }) highlight!: Maybe<ISODateTimeRange>;
    @Inject({ from: 'hasActions', default: true }) readonly hasActions!: boolean;

    @Ref() readonly calendar!: CalendarRef;

    get flatSlots() {
        return Object.values(this.slots).flat();
    }

    get highlightSlot() {
        if (this.highlight) {

            const slot: HighlightSlot = {
                ...this.highlight,
                type: SlotType.Highlight,
                name: '',
            }

            return slot;
        }

        return null;
    }

    get allSlots() {
        if (this.highlight && this.highlightSlot) {
            const isInRange = createDatetimeRangeChecker(this.highlight);
            const slotsWithoutHighlight = this.flatSlots.filter(slot => {
                if (!isInRange(slot.start)) {
                    return true;
                }

                if (isInRange(slot.start) && slot.type !== SlotType.Free) {
                    return true;
                }

                return false;
            });

            return [...slotsWithoutHighlight, this.highlightSlot];
        }
        
        return this.flatSlots;
    }

    get dateWrapper() {
        return this.date
    }

    set dateWrapper(date) {
        this.$emit('update:date', date);
    }

    get weekdays() {
        return ZERO_BASED_WEEKDAYS;
    }

    get isWeek() {
        return this.type === CalendarType.Week
    }

    get isDay() {
        return this.type === CalendarType.Day
    }

    get isShowActions() {
        return this.$route.name !== 'requests';
    }

    @Watch('type')
    loadWeekSlots() {
        // changing from week to day doesn't expand visible days range
        // so we already have all the needed slots
        if (this.isWeek) {
            this.loadVisibleSlots(this.date);
        }
    }

    @Watch('date', { immediate: true })
    loadVisibleSlots(date: ISOCalendarDate) {
        const getRange = this.isWeek ? getWeekRange : getDayRange;

        const visibleRange = getRange(date);
        this.$emit('slots:load', visibleRange);
    }

    focusDate(date: ISOCalendarDate) {
        this.dateWrapper = date;
    }

    formatDateToHumanReadable(date: ISOCalendarDate) {
        return formatDayToHumanText(date);
    }
    
    formatInterval(day: any) {
        const { hour } = day;
        const nextHour = String(hour + 1);

        return `${String(hour).padStart(2, '0')}:00 - ${nextHour.padStart(2, '0')}:00`;
    }

    change(params: CalendarRange) {
        this.$emit('change', params);
    }

    showEvent({ event }: ParsedEvent<Slot>) {
        if (event.type === SlotType.Vacation) {
            this.$emit('select:vacation', event);
            return;
        }

        this.$emit('select:event', event);
    }

    viewDay({ date }: CalendarDate) {
        this.$emit('view:day', date);
        this.focusDate(date);
    }

    removeDay(date: ISOCalendarDate) {
        this.$emit('remove:schedule', date);
    }

    public goPreviousPage() {
        this.calendar.prev();
    }

    public goNextPage() {
        this.calendar.next();
    }
}
